import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useBalance } from "wagmi";
import { Sprout, Trophy, ArrowLeft } from "lucide-react";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { useWalletUser, useApp, ConnectPrompt } from "@/App";
import { cronosMainnet } from "@/wagmi";
import { getValleyConfig, getValleyState, valleyPlant, valleyHarvest, valleyClear, valleyUnlock, valleyBuyDecor, getLeaderboard } from "@/api";
import { play } from "@/lib/sfx";
import { useFlameClaim } from "@/hooks/useFlameClaim";
import { FarmWorld } from "@/components/valley/FarmWorld";
import { ValleyTopBar } from "@/components/valley/ValleyTopBar";
import { SeedShop } from "@/components/valley/SeedShop";
import { LoadCroCard } from "@/components/valley/LoadCroCard";
import { ClaimFlameCard } from "@/components/valley/ClaimFlameCard";
import { CropSprite } from "@/components/valley/CropSprite";
import { plotStage, fmtDuration, STAGE_LABEL } from "@/components/valley/stage";

const short = (a) => `${a.slice(0, 6)}…${a.slice(-4)}`;

function TopFarmers({ rows, me }) {
  return (
    <div className="card-tile" data-testid="valley-top-farmers">
      <h3 className="font-display text-lg font-bold flex items-center gap-2"><Trophy size={18} className="text-amber-400" /> Top Farmers</h3>
      {rows.length === 0 && <div className="text-xs text-slate-500 mt-2">No harvests yet. Be the first.</div>}
      <div className="mt-2 space-y-1">
        {rows.slice(0, 5).map((r, i) => (
          <div key={r.address} data-testid={`valley-top-farmer-${i}`} className={`flex items-center justify-between text-sm px-2 py-1 rounded ${r.address === me ? "bg-orange-500/10" : ""}`}>
            <span className="font-pixel text-[9px] text-amber-400 w-6">{i + 1}</span>
            <span className="font-mono flex-1">{short(r.address)}</span>
            <span className="font-mono text-orange-400">{r.farm_flame.toLocaleString()}</span>
          </div>
        ))}
      </div>
      <Link to="/leaderboard" className="text-xs text-cyan-400 underline mt-2 inline-block" data-testid="valley-full-leaderboard-link">Full leaderboard</Link>
    </div>
  );
}

function SelectedSeedChip({ seed, id, onOpen }) {
  return (
    <button onClick={onOpen} data-testid="valley-selected-seed" className="absolute bottom-3 left-3 z-10 flex items-center gap-2 rounded-2xl bg-black/50 backdrop-blur-md border border-white/10 pl-1 pr-3 py-1 hover:bg-black/65 transition-colors">
      {seed && <CropSprite seed={id} stage="ready" size={40} />}
      <div className="text-left leading-tight">
        <div className="font-pixel text-[7px] text-slate-300/80">SELECTED SEED</div>
        <div className="font-display font-bold text-sm">{seed ? `${seed.name} · ${seed.cost_cro} CRO · ${fmtDuration(seed.grow_sec)}` : "Pick a seed"}</div>
      </div>
    </button>
  );
}

let fxId = 0;

export default function FlameValleyPage() {
  const { isConnected, address, user, refresh } = useWalletUser();
  const { config, showToast, sound, setSound } = useApp();
  const { data: bal } = useBalance({ address, chainId: cronosMainnet.id, query: { enabled: !!address, refetchInterval: 15000 } });
  const [vcfg, setVcfg] = useState(null);
  const [state, setState] = useState(null);
  const [offset, setOffset] = useState(0);
  const [now, setNow] = useState(Date.now() / 1000);
  const [selected, setSelected] = useState("wheat");
  const [shopOpen, setShopOpen] = useState(false);
  const [shopTab, setShopTab] = useState("seeds");
  const [unlockIdx, setUnlockIdx] = useState(null);
  const [fx, setFx] = useState([]);
  const [farmers, setFarmers] = useState([]);
  const [busy, setBusy] = useState(false);

  const apply = useCallback((s) => { setState(s); setOffset(s.server_time - Date.now() / 1000); }, []);
  const load = useCallback(() => address && getValleyState(address).then(apply).catch(() => {}), [address, apply]);
  const loadFarmers = () => getLeaderboard().then((d) => setFarmers(d.top_farmers || [])).catch(() => {});
  const claimer = useFlameClaim({
    address, flame: state?.flame || 0, enabled: !!config?.flame_claims_enabled, hasPendingVoucher: !!user?.pending_voucher,
    showToast, onDone: () => { refresh(); load(); },
  });

  useEffect(() => { getValleyConfig().then(setVcfg); loadFarmers(); }, []);
  useEffect(() => {
    if (!user) return undefined;
    load();
    const id = setInterval(load, 15000);
    return () => clearInterval(id);
  }, [user?.address, load]); // eslint-disable-line
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now() / 1000 + offset), 1000);
    return () => clearInterval(id);
  }, [offset]);

  if (!isConnected) return <ConnectPrompt />;
  if (!state || !vcfg) return <div className="pt-32 text-center text-slate-400" data-testid="valley-loading">Loading Flame Valley…</div>;

  const addFx = (items) => {
    const withIds = items.map((f) => ({ ...f, id: ++fxId }));
    setFx((cur) => [...cur, ...withIds]);
    setTimeout(() => setFx((cur) => cur.filter((f) => !withIds.some((w) => w.id === f.id))), 1600);
  };

  const run = async (fn, okMsg, sfx) => {
    if (busy) return null;
    setBusy(true);
    try {
      const r = await fn();
      apply(r.state);
      play(sound, sfx);
      if (okMsg) showToast(okMsg(r));
      return r;
    } catch (e) {
      play(sound, "error");
      showToast(e?.response?.data?.detail || "Action failed", "error");
      return null;
    } finally { setBusy(false); }
  };

  const openShop = (tab) => { setShopTab(tab); setShopOpen(true); };

  const onPlotClick = async (i) => {
    if (!state.unlocked[i]) {
      if (state.level < vcfg.unlock_min_level) return showToast(`Reach farm level ${vcfg.unlock_min_level} to expand your land`, "error");
      return setUnlockIdx(i);
    }
    const plot = state.plots[i];
    const stage = plotStage(plot, now);
    if (stage === "empty") {
      const seed = vcfg.seeds[selected];
      if (!seed) return openShop("seeds");
      const r = await run(() => valleyPlant(address, i, selected), () => `${seed.name} planted`, "plant");
      if (r) addFx([{ plot: i, type: "dust" }, { plot: i, type: "text", text: `-${seed.cost_cro} CRO`, color: "#ff9a3c" }]);
      return undefined;
    }
    if (stage === "ready") {
      const r = await run(() => valleyHarvest(address, i), (x) => `+${x.flame_gained} FLAME${x.bonus_flame ? ` (incl. +${x.bonus_flame} decor bonus)` : ""} · +${x.xp_gained} XP`, "harvest");
      if (r) {
        addFx([{ plot: i, type: "burst" }, { plot: i, type: "text", text: `+${r.flame_gained} FLAME`, color: "#4ade80" }]);
        if (r.leveled_up) { play(sound, "levelUp"); showToast(`Level up! Farm level ${r.state.level}`); }
        refresh(); loadFarmers();
      }
      return undefined;
    }
    if (stage === "withered") {
      const r = await run(() => valleyClear(address, i), () => "Withered crop cleared", "clear");
      if (r) addFx([{ plot: i, type: "dust" }]);
      return undefined;
    }
    return showToast(`${vcfg.seeds[plot.seed]?.name}: ${STAGE_LABEL[stage]}, ready in ${fmtDuration(plot.ready_at - now)}`);
  };

  const confirmUnlock = async () => {
    const i = unlockIdx;
    setUnlockIdx(null);
    const r = await run(() => valleyUnlock(address, i), () => "New plot unlocked", "unlock");
    if (r) addFx([{ plot: i, type: "dust" }, { plot: i, type: "text", text: `-${vcfg.unlock_cost_cro} CRO`, color: "#ff9a3c" }]);
  };

  const buyDecor = (item) => run(() => valleyBuyDecor(address, item), (r) => `${vcfg.decor[item].name} added · bonus now +${r.state.yield_bonus_pct}%`, "unlock");

  return (
    <div className="pt-20 pb-16">
      <div className="max-w-[1600px] mx-auto px-3 sm:px-4">
        <div className="flex items-end justify-between flex-wrap gap-2 mb-3">
          <div>
            <Link to="/farm" className="text-xs text-slate-400 hover:text-slate-200 flex items-center gap-1" data-testid="valley-back-to-farm"><ArrowLeft size={12} /> FLAME Farm</Link>
            <h1 className="font-display text-4xl sm:text-5xl font-black flex items-center gap-2"><Sprout className="text-emerald-400" /> Flame Valley</h1>
          </div>
          <p className="text-slate-400 text-sm max-w-md">Plant with CRO, harvest FLAME before crops wither, and decorate your farm to boost every harvest.</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-[7fr_3fr] gap-4">
          <div className="relative rounded-3xl overflow-hidden border-4 border-[#3f8f31]/70 shadow-[0_20px_60px_rgba(0,0,0,0.5)]" data-testid="valley-world">
            <ValleyTopBar walletCro={bal ? Number(bal.formatted).toFixed(2) : "—"} state={state} onShop={() => openShop("seeds")}
              claimer={claimer} sound={sound} onToggleSound={() => setSound(!sound)} />
            <div className="pt-24 sm:pt-16 bg-[#5cbf45]">
              <FarmWorld grid={vcfg.grid} plots={state.plots} unlocked={state.unlocked} now={now} onPlotClick={onPlotClick}
                fx={fx} decor={state.decor} unlockCost={vcfg.unlock_cost_cro} onDecorClick={() => openShop("decor")} />
            </div>
            <SelectedSeedChip seed={vcfg.seeds[selected]} id={selected} onOpen={() => openShop("seeds")} />
          </div>
          <div className="space-y-4">
            <LoadCroCard address={address} splitter={config?.burn_splitter} showToast={showToast} onLoaded={(s) => { apply(s); play(sound, "coin"); }} />
            <ClaimFlameCard claimer={claimer} hasPendingVoucher={!!user?.pending_voucher} bonusPct={state.yield_bonus_pct} />
            <TopFarmers rows={farmers} me={address?.toLowerCase()} />
          </div>
        </div>
      </div>

      <SeedShop open={shopOpen} onOpenChange={setShopOpen} tab={shopTab} onTab={setShopTab} seeds={vcfg.seeds} decor={vcfg.decor}
        owned={state.decor} selected={selected} croBalance={state.cro_balance} bonusPct={state.yield_bonus_pct} busy={busy}
        onPick={(id) => { setSelected(id); setShopOpen(false); play(sound, "coin"); }} onBuyDecor={buyDecor} />

      <AlertDialog open={unlockIdx !== null} onOpenChange={(o) => !o && setUnlockIdx(null)}>
        <AlertDialogContent className="bg-[#181A26] border-[#2A2E45] text-slate-100" data-testid="valley-unlock-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display">Unlock this plot?</AlertDialogTitle>
            <AlertDialogDescription className="text-slate-400">Costs {vcfg.unlock_cost_cro} CRO from your farm balance ({state.cro_balance.toFixed(2)} CRO available).</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="valley-unlock-cancel" className="bg-transparent border-[#2A2E45] text-slate-200">Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="valley-unlock-confirm" onClick={confirmUnlock} className="btn-flame">Unlock for {vcfg.unlock_cost_cro} CRO</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
